import React, {
    useContext, useRef,
    useState
} from "react";
import {api} from "../../../../api/API";
import {Grid, IconButton} from "@mui/material";
import {css} from "@emotion/css";
import {blue, deepOrange, grey} from "@mui/material/colors";
import {Product, ProductSearchResult} from "../../../../api/Products";
import {CreateProduct} from "./CreateProduct";
import {CategoryList} from "./CategoryList";
import {ShortCode} from "../../../../api/Library";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {IconCell} from "../hardware/library/IconCell";
import {ProductEdit} from "./ProductEdit";
import {useHwLibraryType} from "../hardware/library/HwLibraryType";
import {useAlternative} from "../../quote/alternative/Alternative";
import {SearchWithAutoComplete} from "./SearchWithAutoComplete";
import {EditContext} from "../../../../misc/scroller/EditProvider";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {InfoOutlined} from "@mui/icons-material";

export type ProductLookupKind = "hardware" | "div-10" | "hardware,div-10" | "frame-anchor" | "manufacture"
const DefaultNResults = 6;

export function ProductLookup(props: {
    placeholder?: string;
    autoFocus?: boolean;
    anchor?: any;
    width?: number;
    align?: "right";
    kind?: ProductLookupKind;
    onBlur?(): void;
    onRender?(): void;
    initialValue?: string;
    resultsInline?: boolean
    isTableCell?: boolean;
    backgroundColor?: string;
    disabled?: boolean;
    renderProductForOpening?: number;
    renderProductForQuoteOpening?: number;
} & (
{
    onSelect(product: ProductSearchResult): Promise<void>
} | {
    onSelect(product: ProductSearchResult | ShortCode): Promise<void>
    includeShortCodesForProject: number;
})) {

    // @ts-ignore
    const projectShortCode = props.includeShortCodesForProject;
    const [showEdit, setShowEdit] = useState<Product>();
    const edit = useContext(EditContext);

    const [category, setCategory] = useState<number>();
    const [createProduct, setCreateProduct] = useState(false);

    const renderProductForOpeningRef = useSyncedRef(props.renderProductForOpening)
    const renderProductForQuoteOpeningRef = useSyncedRef(props.renderProductForQuoteOpening)

    const libraryType = useHwLibraryType();
    const alternative = useAlternative();
    const searchStrRef = useRef(props.initialValue || "");
    const signal = useRef<AbortController>();
    const offsetRef = useRef(0);

    return (
        <>
            <SearchWithAutoComplete<ProductRowInfo>
                placeholder={props.placeholder}
                autoFocus={props.autoFocus}
                onFocus={() => {
                    if(!props.isTableCell) {
                        edit.onSelect.emit(null); // just in case a cell is selected
                    }
                }}
                anchor={props.anchor}
                width={props.width}
                align={props.align}
                onBlur={props.onBlur}
                onRender={props.onRender}
                initialValue={props.initialValue}
                resultsInline={props.resultsInline}
                isTableCell={props.isTableCell}
                backgroundColor={props.backgroundColor}
                disabled={props.disabled}
                onSelect={v => {
                    if(v.type === "product") {
                        api.products.searchSignal({product: v.value.id, searchQuery: searchStrRef.current})
                    }

                    return props.onSelect(v.value as any)
                }}
                showingPopup={!!showEdit||createProduct}
                onSearch={async (input) => {

                    if(searchStrRef.current !== input.search) {
                        offsetRef.current = 0;
                    }

                    searchStrRef.current = input.search;

                    if(signal.current) {
                        signal.current.abort("cancelled");
                    }

                    signal.current = new AbortController();

                    let shortCodes: ShortCode[] = [];
                    const searchResult = api.products.search({
                        productSearch: input.search,
                        kind: props.kind,
                        category: category,
                        offset: offsetRef.current,
                        renderProductForOpening: renderProductForOpeningRef.current,
                        renderProductForQuoteOpening: renderProductForQuoteOpeningRef.current,
                    }, signal.current)

                    if(projectShortCode) {
                        shortCodes = await api.hardwareLibrary.getShortCodes({
                            project: projectShortCode,
                            alternative: alternative,
                            search: input.search,
                            type: libraryType,
                        });

                        if(!input.search) {
                            let have = 0;

                            // restrict the number of shortcodes during initial result if there's no search
                            shortCodes = shortCodes.filter((c, index) => {
                                let count = c.items?.length || 0;
                                if (count + have > 8) {
                                    return false;
                                }

                                have += count;
                                return true;
                            })
                        }
                    }

                    try {
                        const productList = await searchResult;

                        return [
                            ...shortCodes.map(c => ({type: "shortcode" as "shortcode", value: c})),
                            ...productList.data.map(c => ({type: "product" as "product", value: c})),
                        ]
                    } catch(e: any) {
                        if(e.toString() === "cancelled") return [];
                        throw e;
                    }
                }}
                onSearchDeps={[category, libraryType, alternative, projectShortCode]}
                renderResultRow={(input) => {
                    const d = input.value;

                    if(d.type === "product") {
                        return <ProductRow
                            key={input.index}
                            value={d.value}
                            onSelect={input.onSelect}
                            onEdit={async (v) => {
                                const product = await api.products.getById(v.id)
                                setShowEdit(product)
                                input.onFullReset()
                            }}
                            selected={input.selected ? input.index : -1}
                            index={input.index}
                        />
                    }

                    return <ShortCodeRow
                        key={input.index}
                        value={d.value}
                        onSelect={input.onSelect}
                        index={input.index}
                    />
                }}

                renderFooter={(info) => {
                    const productResultCount = info.results.filter(r => r.type === "product").length;

                    return (
                        <>
                            <Grid container wrap="nowrap" spacing={1}>
                                <Grid item>
                                    Find:
                                </Grid>
                                <Grid item xs>
                                    <CategoryList onSelect={setCategory} selected={category} search={info.search} />
                                </Grid>
                                {(productResultCount >= DefaultNResults || offsetRef.current > 0) && <Grid item>
                                    {offsetRef.current > 0 && <button className={categoryButtonStyle} onClick={() => {
                                            offsetRef.current -= DefaultNResults;
                                            offsetRef.current = Math.max(offsetRef.current, 0);
                                            info.onRefreshSearch()
                                        }}>Prev</button>
                                    }
                                    {productResultCount >= DefaultNResults && <button className={categoryButtonStyle} onClick={e => {
                                            e.stopPropagation();
                                            e.preventDefault();

                                            offsetRef.current += DefaultNResults;
                                            info.onRefreshSearch()
                                        }}>More</button>}
                                </Grid>}
                                <Grid item>
                                    <button onClick={() => {
                                        info.onFullReset()
                                        setCreateProduct(true);
                                    }} className={categoryButtonStyle}>Create Product</button>
                                </Grid>
                            </Grid>
                        </>
                    )
                }}

                renderMisc={(ctrl) => {
                    return <>
                        {showEdit && <ProductEdit mode={toCreateProductMode(props.kind)} value={showEdit} onClose={product => {
                            setShowEdit(undefined);
                            if(!product) return;

                            ctrl.onSelect({
                                type: "product",
                                value: Object.assign({lastUnitCostCents: 0}, product),
                            })
                        }} />}
                        {createProduct && <CreateProduct mode={toCreateProductMode(props.kind)} onDone={value => {
                            setCreateProduct(false)
                            ctrl.onSelect({
                                type: 'product',
                                value: Object.assign({}, value, {
                                    lastUnitCostCents: 0,
                                }),
                            })
                        }} onCancel={() => {
                            setCreateProduct(false)
                            ctrl.onFullReset()
                        }} />}
                    </>
                }}
            />

        </>
    )
}

function toCreateProductMode(input?: ProductLookupKind) {
    switch(input) {
        case "div-10": return "div-10" as "div-10";
        case "hardware": return "hardware" as "hardware";
        case "frame-anchor": return "frame-anchor" as "frame-anchor";
        default: return null;
    }
}

type ProductRowInfo = ({
        type: "shortcode";
        value: ShortCode
} | {
    type: "product";
    value: ProductSearchResult
})

function ProductRow(props: {
    value: ProductSearchResult,
    onSelect(value: {type: "product", value: ProductSearchResult}): void;
    onEdit(value: ProductSearchResult): void;
    selected: number;
    index: number;
}) {
    const d = props.value;

    return (
        <>
            <tr key="base" onClick={() => props.onSelect({type: "product", value: d})}
                className={resultRowStyle}
                style={{
                    backgroundColor:
                        d.discontinued ? deepOrange["50"] :
                        props.selected === props.index ? grey["300"] : undefined,
                }}>

                <td>
                    {d.discontinued ? <span style={{textDecoration: "none"}}>Discontinued</span> : null}
                    <span style={{textDecoration: d.discontinued ? "line-through" : undefined}}>{d.categoryName || ""}</span>
                </td>
                <td style={{textDecoration: d.discontinued ? "line-through" : undefined}}>{d.series}</td>
                <td style={{textDecoration: d.discontinued ? "line-through" : undefined}}>{d.productCode}</td>
                <td style={{textDecoration: d.discontinued ? "line-through" : undefined}}>{d.name}</td>
                <td style={{textDecoration: d.discontinued ? "line-through" : undefined}}>{d.finish}</td>
                <td style={{textDecoration: d.discontinued ? "line-through" : undefined}}>{d.manufacturerName}</td>
                <td style={{padding: 0}}>
                    {d.fuzzy ? <span style={{color: blue["200"], fontSize: "0.7rem"}}>
                        <InfoOutlined fontSize="inherit" />
                    </span> : null}
                </td>
                <IconCell className="more-detail">
                    <IconButton onClick={e => {
                        e.stopPropagation();
                        props.onEdit(props.value);
                    }}>
                        <MoreHorizIcon fontSize="inherit" />
                    </IconButton>
                </IconCell>
            </tr>
            {props.value.discontinuedReplaceWith.map((r,index) => (
                <tr key={`replace-${r.id}`} onClick={() => props.onSelect({type: "product", value: r as any})}
                    className={resultRowStyle}
                    style={{
                        backgroundColor: props.selected === props.index ? grey["300"] : undefined,
                    }}>
                    <td></td>
                    <td>
                        {index === 0 && "Alternative"}
                        <div style={{paddingLeft: 16}}>{r.productCode}</div></td>
                    <td></td>
                    <td>{r.finish}</td>
                    <td>{r.manufacturerName}</td>
                    <IconCell className="more-detail">
                        <IconButton onClick={e => {
                            e.stopPropagation();
                            props.onEdit(r as any);
                        }}>
                            <MoreHorizIcon fontSize="inherit" />
                        </IconButton>
                    </IconCell>
                </tr>
            ))}
        </>
    )
}

function ShortCodeRow(props: {
    value: ShortCode,
    onSelect(value: {type: "shortcode", value: ShortCode}): void;
    index: number;
}) {
    const [hover, setHover] = useState(false);
    return (
        <>
            {(props.value.items || []).map((v, index) =>
                <tr
                    key={props.index + "-" + v.id}
                    style={{
                        backgroundColor: hover ? grey["200"] : undefined,
                    }}
                    className={resultRowStyle}
                    onClick={() => props.onSelect({type: "shortcode", value: props.value})}
                    onMouseOver={() => setHover(true)}
                    onMouseOut={() => setHover(false)}
                >
                    {index === 0 && <td rowSpan={props.value.items?.length||1} style={{
                        borderRight: "1px solid " + grey["200"]
                    }}>
                        <div style={{fontSize: "0.7rem", color: grey["600"]}}>Shortcode</div>
                        {props.value.value}
                    </td>}
                    <td>{v.productName}</td>
                    <td>{v.series}</td>
                    <td>{v.productCode}</td>
                    <td>{v.finish}</td>
                    <td style={{textAlign: "right"}}>{v.qty}</td>
                    <td />
                    <td />
                </tr>)}
        </>
    )
}

export const categoryButtonStyle = css({
    padding: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 2,

    marginLeft: 8,
    marginRight: 8,
    marginBottom: 2,

    cursor: "pointer",

    backgroundColor: "transparent",
    border: "none",
    borderBottom: "1px solid",
    color: grey["700"]
})

const resultRowStyle = css({

  "& > td": {
      borderBottom: "1px solid " + grey["200"],
      paddingLeft: 8, paddingRight: 8,
      paddingTop: 4, paddingBottom: 4,
      fontSize: "0.8rem",
      cursor: "pointer",
  },

    "&:hover > td": {
        backgroundColor: grey["200"],
    },

    "& .more-detail": {
      opacity: 0,
    },

    "&:hover .more-detail": {
      opacity: 1,
    }
})
